import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap } from 'rxjs';

export interface NotificationSubscription {
  id: number;
  user_id: number;
  subscription_type: 'watchlist' | 'benchmark';
  watchlist_id?: number;
  benchmark_id?: number;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SubscriptionResponse {
  payload: {
    subscriptions: NotificationSubscription[];
  };
  message: string;
}

export interface SubscriptionToggleResponse {
  payload: {
    subscription: NotificationSubscription;
  };
  message: string;
}

export interface SubscriptionStatusResponse {
  payload: {
    subscription_type: string;
    watchlist_id?: number;
    benchmark_id?: number;
    is_subscribed: boolean;
  };
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationSubscriptionService {

  private subscriptionsSubject = new BehaviorSubject<NotificationSubscription[]>([]);
  subscriptions$ = this.subscriptionsSubject.asObservable();

  constructor(private http: HttpClient) {
    this.loadSubscriptions();
  }

  private getAuthHeaders() {
    return {
      Authorization: "Bearer " + localStorage.getItem("current_user_token")
    };
  }

  /**
   * Load all subscriptions for the current user
   */
  loadSubscriptions(): void {
    this.http.get<SubscriptionResponse>('/backend/notifications/subscriptions', {
      headers: this.getAuthHeaders()
    }).subscribe({
      next: (res) => {
        this.subscriptionsSubject.next(res.payload.subscriptions || []);
      },
      error: (err) => {
        console.error('Failed to load subscriptions:', err);
      }
    });
  }

  /**
   * Get all subscriptions
   */
  getSubscriptions(): Observable<SubscriptionResponse> {
    return this.http.get<SubscriptionResponse>('/backend/notifications/subscriptions', {
      headers: this.getAuthHeaders()
    });
  }

  /**
   * Toggle subscription for a watchlist
   */
  toggleWatchlistSubscription(watchlistId: number, enabled: boolean): Observable<SubscriptionToggleResponse> {
    return this.http.post<SubscriptionToggleResponse>('/backend/notifications/subscriptions/toggle', {
      subscription_type: 'watchlist',
      watchlist_id: watchlistId,
      enabled: enabled
    }, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap(() => this.loadSubscriptions())
    );
  }

  /**
   * Toggle subscription for a benchmark
   */
  toggleBenchmarkSubscription(benchmarkId: number, enabled: boolean): Observable<SubscriptionToggleResponse> {
    return this.http.post<SubscriptionToggleResponse>('/backend/notifications/subscriptions/toggle', {
      subscription_type: 'benchmark',
      benchmark_id: benchmarkId,
      enabled: enabled
    }, {
      headers: this.getAuthHeaders()
    }).pipe(
      tap(() => this.loadSubscriptions())
    );
  }

  /**
   * Check if subscribed to a specific watchlist.
   * DEFAULT: Returns true if no subscription record exists (opt-out model).
   * Only returns false if an explicit record exists with enabled=false.
   */
  isSubscribedToWatchlist(watchlistId: number): boolean {
    const subscriptions = this.subscriptionsSubject.getValue();
    const subscription = subscriptions.find(
      s => s.subscription_type === 'watchlist' && s.watchlist_id === watchlistId
    );
    // Default to subscribed (true) if no record exists
    if (!subscription) {
      return true;
    }
    return subscription.enabled;
  }

  /**
   * Check if subscribed to a specific benchmark.
   * DEFAULT: Returns true if no subscription record exists (opt-out model).
   * Only returns false if an explicit record exists with enabled=false.
   */
  isSubscribedToBenchmark(benchmarkId: number): boolean {
    const subscriptions = this.subscriptionsSubject.getValue();
    const subscription = subscriptions.find(
      s => s.subscription_type === 'benchmark' && s.benchmark_id === benchmarkId
    );
    // Default to subscribed (true) if no record exists
    if (!subscription) {
      return true;
    }
    return subscription.enabled;
  }

  /**
   * Get subscription status from API
   */
  getSubscriptionStatus(subscriptionType: 'watchlist' | 'benchmark', id: number): Observable<SubscriptionStatusResponse> {
    const params = subscriptionType === 'watchlist'
      ? `subscription_type=watchlist&watchlist_id=${id}`
      : `subscription_type=benchmark&benchmark_id=${id}`;

    return this.http.get<SubscriptionStatusResponse>(
      `/backend/notifications/subscriptions/status?${params}`,
      { headers: this.getAuthHeaders() }
    );
  }
}
